import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { EvaluationElements } from './components/EvaluationElements';
import { getSelectedEvaluationTypes, selectionIncludesFeedback360 } from './evaluationSelection';
import {
    buildTemplateOptionsByType,
    getTypesWithUnavailableTemplate,
    getTypesWithUnresolvableTemplate,
    isTemplateStillAvailable,
} from './templateOptions';
import { useEvaluationElementsData } from './useEvaluationElementsData';
import { useEvaluationElementsSelection } from './useEvaluationElementsSelection';
import { useSaveEvaluationElements } from './useSaveEvaluationElements';

import type { TemplateAvailabilityByType } from './templateOptions';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';

import {
    evaluationElementsLiveSelectionAtom,
    evaluationElementsOnNextAtom,
} from '@/atoms/cycleEvaluation';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations, useNavigate, useRegisterPageAction } from '@/hooks';
import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import { RouteName } from '@/navigation/types';
import { showWarningToast } from '@/plugins/Toast';

export const EvaluationElementsPage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const {
        group,
        managerTemplates,
        feedback360Templates,
        selfAssessmentTemplates,
        amelioManagerTemplates,
        amelioFeedback360Templates,
        amelioSelfAssessmentTemplates,
        cycleEvaluationByType,
        updateCycleEvaluationGroup,
        createCycleEvaluationForGroup,
        updateCycleEvaluation,
        importTemplateInCycleQuestionnaire,
        isLoading,
    } = useEvaluationElementsData(cycleEvaluationGroupId);

    const { selection, handleToggleType, handleTemplateChange, handleToggleOrganizationalValues } =
        useEvaluationElementsSelection(group, cycleEvaluationByType);

    const [typesWithFailedImport, setTypesWithFailedImport] = useState<
        readonly EvaluationTypeValue[]
    >([]);

    const hasAnnouncedUnavailableTemplateRef = useRef(false);

    const setLiveSelection = useSetAtom(evaluationElementsLiveSelectionAtom);
    const includesFeedback360 = selectionIncludesFeedback360(selection);
    const feedback360TemplateId = selection.templateIds[EvaluationType.EVALUATION_360];

    const isFeedback360TemplateMissing =
        includesFeedback360 &&
        !feedback360Templates.isLoading &&
        !amelioFeedback360Templates.isLoading &&
        !isTemplateStillAvailable(
            feedback360TemplateId,
            feedback360Templates.data,
            amelioFeedback360Templates.data,
        );

    const templateAvailabilityByType: TemplateAvailabilityByType = useMemo(
        () => ({
            [EvaluationType.MANAGER_EVALUATION]: {
                pages: managerTemplates.data,
                amelioTemplates: amelioManagerTemplates.data,
                isAmelioSuccess: amelioManagerTemplates.isSuccess,
                isSuccess: managerTemplates.isSuccess,
            },
            [EvaluationType.EVALUATION_360]: {
                pages: feedback360Templates.data,
                amelioTemplates: amelioFeedback360Templates.data,
                isAmelioSuccess: amelioFeedback360Templates.isSuccess,
                isSuccess: feedback360Templates.isSuccess,
            },
            [EvaluationType.SELF_EVALUATION]: {
                pages: selfAssessmentTemplates.data,
                amelioTemplates: amelioSelfAssessmentTemplates.data,
                isAmelioSuccess: amelioSelfAssessmentTemplates.isSuccess,
                isSuccess: selfAssessmentTemplates.isSuccess,
            },
        }),
        [
            managerTemplates.data,
            managerTemplates.isSuccess,
            amelioManagerTemplates.data,
            amelioManagerTemplates.isSuccess,
            feedback360Templates.data,
            feedback360Templates.isSuccess,
            amelioFeedback360Templates.data,
            amelioFeedback360Templates.isSuccess,
            selfAssessmentTemplates.data,
            selfAssessmentTemplates.isSuccess,
            amelioSelfAssessmentTemplates.data,
            amelioSelfAssessmentTemplates.isSuccess,
        ],
    );

    const storedTemplateVerdictParams = useMemo(
        () => ({
            selectedTypes: getSelectedEvaluationTypes(selection),
            templateIds: selection.templateIds,
            availabilityByType: templateAvailabilityByType,
        }),
        [selection, templateAvailabilityByType],
    );

    const derivedTypesWithUnavailableTemplate = useMemo(
        () => getTypesWithUnavailableTemplate(storedTemplateVerdictParams),
        [storedTemplateVerdictParams],
    );

    const typesWithUnavailableTemplate = useMemo(
        () =>
            Array.from(new Set([...derivedTypesWithUnavailableTemplate, ...typesWithFailedImport])),
        [derivedTypesWithUnavailableTemplate, typesWithFailedImport],
    );

    const handleTemplateImportFailed = useCallback(
        (types: readonly EvaluationTypeValue[]) =>
            setTypesWithFailedImport((current) => Array.from(new Set([...current, ...types]))),
        [],
    );

    const typesWithUnresolvableTemplate = useMemo(
        () => getTypesWithUnresolvableTemplate(storedTemplateVerdictParams),
        [storedTemplateVerdictParams],
    );

    const hasUnavailableStoredTemplate = derivedTypesWithUnavailableTemplate.length > 0;

    useEffect(() => {
        if (!cycleEvaluationGroupId) {
            return;
        }

        setLiveSelection({
            cycleEvaluationGroupId,
            includesFeedback360,
            isFeedback360TemplateMissing,
        });
    }, [
        setLiveSelection,
        cycleEvaluationGroupId,
        includesFeedback360,
        isFeedback360TemplateMissing,
    ]);

    const templateOptionsByType = useMemo(
        () =>
            buildTemplateOptionsByType({
                noAdditionalTemplateLabel: t(
                    'cycleEvaluationEvaluationElements.template.noAdditionalTemplate',
                ),
                managerTemplatePages: managerTemplates.data,
                feedback360TemplatePages: feedback360Templates.data,
                selfAssessmentTemplatePages: selfAssessmentTemplates.data,
                amelioTemplatesByType: {
                    [EvaluationType.MANAGER_EVALUATION]: amelioManagerTemplates.data,
                    [EvaluationType.EVALUATION_360]: amelioFeedback360Templates.data,
                    [EvaluationType.SELF_EVALUATION]: amelioSelfAssessmentTemplates.data,
                },
            }),
        [
            t,
            managerTemplates.data,
            feedback360Templates.data,
            selfAssessmentTemplates.data,
            amelioManagerTemplates.data,
            amelioFeedback360Templates.data,
            amelioSelfAssessmentTemplates.data,
        ],
    );

    const handleManageTemplates = useCallback(() => {
        navigate({ path: RouteName.ManageTemplates });
    }, [navigate]);

    const handleTemplateChangeAndClearFailure = useCallback(
        (type: EvaluationTypeValue, templateId: string) => {
            setTypesWithFailedImport((current) =>
                current.filter((failedType) => failedType !== type),
            );
            handleTemplateChange(type, templateId);
        },
        [handleTemplateChange],
    );

    useEffect(() => {
        if (hasUnavailableStoredTemplate && !hasAnnouncedUnavailableTemplateRef.current) {
            showWarningToast({
                message: t(
                    'cycleEvaluationEvaluationElements.validation.templateNoLongerAvailable',
                ),
            });
        }

        hasAnnouncedUnavailableTemplateRef.current = hasUnavailableStoredTemplate;
    }, [hasUnavailableStoredTemplate, t]);

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);

    const onNext = useSaveEvaluationElements({
        isReadOnly,
        selection,
        typesWithUnavailableTemplate,
        typesWithUnresolvableTemplate,
        group,
        cycleEvaluationGroupId,
        cycleEvaluationByType,
        createCycleEvaluationForGroup,
        updateCycleEvaluation,
        importTemplateInCycleQuestionnaire,
        updateCycleEvaluationGroup,
        onTemplateImportFailed: handleTemplateImportFailed,
    });

    useRegisterPageAction(evaluationElementsOnNextAtom, onNext);

    return (
        <EvaluationElements
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            unavailableTypes={[]}
            selection={selection}
            typesWithUnavailableTemplate={typesWithUnavailableTemplate}
            templateOptionsByType={templateOptionsByType}
            onToggleType={handleToggleType}
            onTemplateChange={handleTemplateChangeAndClearFailure}
            onToggleOrganizationalValues={handleToggleOrganizationalValues}
            onManageTemplates={handleManageTemplates}
        />
    );
};
